import { useApolloClient } from '@apollo/client';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import { FunctionComponent, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { getEnv } from '../../../../config/env';
import { firebaseAuth } from '../../../../firebase/firebase.service';
import { CURRENT_USER } from '../../../graphql/query/currentUserQuery.graphql';

interface ImageUploadProps {
  maxImages: number;
}

export const ImageUploader: FunctionComponent<ImageUploadProps> = ({
  maxImages,
}) => {
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  const apolloClient = useApolloClient();

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    setUploading(true);
    setMessage('');

    const formData = new FormData();
    acceptedFiles.forEach((file) => {
      formData.append('files', file);
    });

    try {
      const token = await firebaseAuth.currentUser?.getIdToken();

      await axios.post(`${getEnv('REACT_APP_API_URL')}/api/images`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      apolloClient.refetchQueries({ include: [CURRENT_USER] });
    } catch (error) {
      setMessage("L'envoie du fichier a échoué. Veuillez réessayer.");
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': [],
    },
    maxFiles: maxImages,
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: '2px dashed',
        borderColor: 'grey.400',
        p: 3,
        borderRadius: 2,
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'border-color 0.2s ease-in-out',
        '&:hover': {
          borderColor: 'primary.main',
        },
      }}
    >
      <input {...getInputProps()} />
      {isDragActive ? (
        <p>Déposer vos images ici</p>
      ) : (
        <Box>
          <AddCircleIcon color="primary" fontSize="large" />
          <Typography>Ajouter une image</Typography>
        </Box>
      )}

      {uploading && <p className="mt-2 text-blue-500">Chargement...</p>}
      {message && <p className="mt-2 text-green-600">{message}</p>}
    </Box>
  );
};
